import * as React from "react";
import {useState, useRef, useEffect, useCallback} from "react";
import {createPortal} from "react-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faAt} from "@fortawesome/pro-regular-svg-icons";
import {useIntl} from "react-intl";

interface TemplateVariable {
    key: string;
    labelKey: string;
}

// Variable names must match the Contact and CustomerAccount read model property names
const TEMPLATE_VARIABLES: TemplateVariable[] = [
    // Contact variables
    {key: "contact.givenName", labelKey: "templateVariable.contact.givenName"},
    {key: "contact.familyName", labelKey: "templateVariable.contact.familyName"},
    {key: "contact.fullName", labelKey: "templateVariable.contact.fullName"},
    {key: "contact.emailAddress", labelKey: "templateVariable.contact.emailAddress"},
    {key: "contact.salutation", labelKey: "templateVariable.contact.salutation"},
    {key: "contact.teamName", labelKey: "templateVariable.contact.teamName"},
    // CustomerAccount variables
    {key: "customerAccount.customerName", labelKey: "templateVariable.customerAccount.customerName"},
    // Other variables
    {key: "subscriptionPreferencesUrl", labelKey: "templateVariable.subscriptionPreferencesUrl"},
];

interface TemplateVariablePickerProps {
    onInsert: (variable: string) => void;
}

interface DropdownPosition {
    top: number;
    right: number;
}

const TemplateVariablePicker: React.FC<TemplateVariablePickerProps> = ({onInsert}) => {
    const intl = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState<DropdownPosition>({top: 0, right: 0});
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const updatePosition = useCallback(() => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + 4,
                right: window.innerWidth - rect.right,
            });
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            updatePosition();
            window.addEventListener("scroll", updatePosition, true);
            window.addEventListener("resize", updatePosition);
            return () => {
                window.removeEventListener("scroll", updatePosition, true);
                window.removeEventListener("resize", updatePosition);
            };
        }
    }, [isOpen, updatePosition]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                buttonRef.current && !buttonRef.current.contains(target) &&
                dropdownRef.current && !dropdownRef.current.contains(target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleInsertVariable = (variable: TemplateVariable) => {
        onInsert(`{{${variable.key}}}`);
        setIsOpen(false);
    };

    const dropdownContent = isOpen ? (
        <div
            ref={dropdownRef}
            className="fixed z-9999 w-72 bg-white border border-gray-200 rounded-md shadow-lg max-h-[70vh] overflow-y-auto"
            style={{
                top: position.top,
                right: position.right,
            }}
        >
            <div className="py-1">
                <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {intl.formatMessage({id: "templateVariable.group.contact"})}
                </div>
                {TEMPLATE_VARIABLES.filter(v => v.key.startsWith("contact.")).map((variable) => (
                    <button
                        key={variable.key}
                        type="button"
                        onClick={() => handleInsertVariable(variable)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex justify-between items-center"
                    >
                        <span>{intl.formatMessage({id: variable.labelKey})}</span>
                        <code className="text-xs text-gray-400 bg-gray-100 px-1 rounded-sm">{`{{${variable.key}}}`}</code>
                    </button>
                ))}

                <div className="border-t border-gray-100 mt-1"/>

                <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {intl.formatMessage({id: "templateVariable.group.customerAccount"})}
                </div>
                {TEMPLATE_VARIABLES.filter(v => v.key.startsWith("customerAccount.")).map((variable) => (
                    <button
                        key={variable.key}
                        type="button"
                        onClick={() => handleInsertVariable(variable)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex justify-between items-center"
                    >
                        <span>{intl.formatMessage({id: variable.labelKey})}</span>
                        <code className="text-xs text-gray-400 bg-gray-100 px-1 rounded-sm">{`{{${variable.key}}}`}</code>
                    </button>
                ))}

                <div className="border-t border-gray-100 mt-1"/>

                <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {intl.formatMessage({id: "templateVariable.group.other"})}
                </div>
                {TEMPLATE_VARIABLES.filter(v => !v.key.includes(".")).map((variable) => (
                    <button
                        key={variable.key}
                        type="button"
                        onClick={() => handleInsertVariable(variable)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex justify-between items-center"
                    >
                        <span>{intl.formatMessage({id: variable.labelKey})}</span>
                        <code className="text-xs text-gray-400 bg-gray-100 px-1 rounded-sm">{`{{${variable.key}}}`}</code>
                    </button>
                ))}
            </div>
        </div>
    ) : null;

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-sm"
                title={intl.formatMessage({id: "richTextEditor.insertVariable"})}
            >
                <FontAwesomeIcon icon={faAt} className="w-3 h-3"/>
                <span>{intl.formatMessage({id: "richTextEditor.insertVariable"})}</span>
                <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3"/>
            </button>
            {dropdownContent && createPortal(dropdownContent, document.body)}
        </>
    );
};

export default TemplateVariablePicker;
